/**
 * Rich diagram preview — renders card-like rectangles with titles and connection lines.
 */
export default function DiagramThumbnail({ miniNodes = [], miniEdges = [], width = 300, height = 100 }) {
  if (!miniNodes || miniNodes.length === 0) {
    return (
      <div className="w-full flex items-center justify-center bg-gray-50 text-gray-300" style={{ height }}>
        <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <rect x="3" y="3" width="18" height="18" rx="3" />
          <path d="M3 9h18M9 3v18" />
        </svg>
      </div>
    );
  }

  // Node dimensions in source space
  const nodeW = 120;
  const nodeH = 32;

  // Calculate bounds
  const xs = miniNodes.map(n => n.x);
  const ys = miniNodes.map(n => n.y);
  const minX = Math.min(...xs);
  const minY = Math.min(...ys);
  const maxX = Math.max(...xs) + nodeW;
  const maxY = Math.max(...ys) + nodeH;
  const pad = 12;
  const srcW = maxX - minX + pad * 2;
  const srcH = maxY - minY + pad * 2;

  // Scale to fit
  const scale = Math.min(width / srcW, height / srcH, 1);
  const offsetX = (width - srcW * scale) / 2 - minX * scale + pad * scale;
  const offsetY = (height - srcH * scale) / 2 - minY * scale + pad * scale;

  const rw = Math.max(nodeW * scale, 24);
  const rh = Math.max(nodeH * scale, 10);
  const fontSize = Math.max(Math.min(rw / 12, 7), 3.5);
  const barH = Math.max(rh * 0.18, 1.5);

  // Node position map for edges
  const nodeMap = {};
  miniNodes.forEach((n, i) => {
    const id = n.id || `n${i}`;
    nodeMap[id] = {
      x: n.x * scale + offsetX,
      y: n.y * scale + offsetY,
      cx: n.x * scale + offsetX + rw / 2,
      cy: n.y * scale + offsetY + rh / 2,
    };
  });

  return (
    <svg width="100%" height={height} viewBox={`0 0 ${width} ${height}`} className="w-full" preserveAspectRatio="xMidYMid meet">
      <rect width={width} height={height} fill="#FAFAFA" />
      {/* Edges */}
      {(miniEdges || []).map((e, i) => {
        const s = nodeMap[e.source];
        const t = nodeMap[e.target];
        if (!s || !t) return null;
        return (
          <line
            key={`e${i}`}
            x1={s.cx}
            y1={s.cy}
            x2={t.cx}
            y2={t.cy}
            stroke="#D1D5DB"
            strokeWidth={Math.max(scale * 1.2, 0.5)}
          />
        );
      })}
      {/* Edge arrowheads */}
      {(miniEdges || []).map((e, i) => {
        const s = nodeMap[e.source];
        const t = nodeMap[e.target];
        if (!s || !t) return null;
        const dx = t.cx - s.cx;
        const dy = t.cy - s.cy;
        const len = Math.sqrt(dx * dx + dy * dy);
        if (len < 5) return null;
        const nx = dx / len;
        const ny = dy / len;
        const arrowLen = Math.min(4 * scale, 5);
        const ax = t.cx - nx * (rh / 2 + 1);
        const ay = t.cy - ny * (rh / 2 + 1);
        return (
          <polygon
            key={`a${i}`}
            points={`${ax},${ay} ${ax - nx * arrowLen + ny * arrowLen * 0.5},${ay - ny * arrowLen - nx * arrowLen * 0.5} ${ax - nx * arrowLen - ny * arrowLen * 0.5},${ay - ny * arrowLen + nx * arrowLen * 0.5}`}
            fill="#9CA3AF"
          />
        );
      })}
      {/* Nodes as card rectangles */}
      {miniNodes.map((node, i) => {
        const id = node.id || `n${i}`;
        const pos = nodeMap[id];
        if (!pos) return null;
        const color = node.color || "#6366F1";
        const title = node.title || "";
        return (
          <g key={id}>
            {/* Card shadow */}
            <rect
              x={pos.x + 0.5}
              y={pos.y + 0.5}
              width={rw}
              height={rh}
              rx={Math.max(rh * 0.15, 2)}
              fill="#E5E7EB"
              opacity="0.4"
            />
            {/* Card body */}
            <rect
              x={pos.x}
              y={pos.y}
              width={rw}
              height={rh}
              rx={Math.max(rh * 0.15, 2)}
              fill="white"
              stroke="#E5E7EB"
              strokeWidth={Math.max(scale * 0.5, 0.3)}
            />
            {/* Color bar at top */}
            <rect
              x={pos.x}
              y={pos.y}
              width={rw}
              height={barH}
              rx={Math.max(rh * 0.15, 2)}
              fill={color}
            />
            {/* Mask bottom corners of bar */}
            <rect
              x={pos.x}
              y={pos.y + barH * 0.5}
              width={rw}
              height={barH * 0.5}
              fill={color}
            />
            {/* Title text */}
            {title && fontSize >= 3.5 && (
              <text
                x={pos.x + rw / 2}
                y={pos.y + rh / 2 + fontSize * 0.35 + barH * 0.3}
                textAnchor="middle"
                fill="#374151"
                fontSize={fontSize}
                fontWeight="600"
                fontFamily="system-ui, sans-serif"
              >
                {title.length > 14 ? title.slice(0, 12) + ".." : title}
              </text>
            )}
          </g>
        );
      })}
    </svg>
  );
}
